import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { ntxAPI } from '../../../API/axiosNeatex';

export interface TaskTimeEntrie {
  id: string;
  taskId: string;
  resourceId: string;
  resourceName?: string;
  date: string;
  hours: number;
  notes: string;
  approved: boolean;
}

// ==================== Async Thunks ====================

// 📥 جلب جميع السجلات حسب المهمة
export const getTaskTimeEntriesByTaskId = createAsyncThunk(
  'TaskTimeEntrie/getByTaskId',
  async (taskId: string, { rejectWithValue }) => {
    try {
      const { data } = await ntxAPI.get(`Task/GetTaskTimeEntrieByTaskId?taskId=${taskId}`);
      if (data?.succeeded) return data.data;
      return [];
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

// ➕ إضافة سجل وقت
export const addTaskTimeEntrie = createAsyncThunk(
  'TaskTimeEntrie/add',
  async (item: Omit<TaskTimeEntrie, 'id' | 'createdAt' | 'updatedAt' | 'resourceName'>, { rejectWithValue }) => {
    try {
      const { data } = await ntxAPI.post('Task/AddTaskTimeEntrie', item);
      if (data?.succeeded) return data.data;
      return rejectWithValue(data?.message || 'فشل في إضافة السجل');
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

// ✏️ تعديل سجل وقت
export const updateTaskTimeEntrie = createAsyncThunk(
  'TaskTimeEntrie/update',
  async (item: TaskTimeEntrie, { rejectWithValue }) => {
    try {
      const { data } = await ntxAPI.post('Task/UpdateTaskTimeEntrie', item);
      if (data?.succeeded) return data.data;
      return rejectWithValue(data?.message || 'فشل في تعديل السجل');
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

// 🗑️ حذف سجل وقت
export const deleteTaskTimeEntrie = createAsyncThunk(
  'TaskTimeEntrie/delete',
  async (id: string, { rejectWithValue }) => {
    try {
      const { data } = await ntxAPI.post(`Task/DeleteTaskTimeEntrie?id=${id}`);
      if (data?.succeeded) return id;
      return rejectWithValue(data?.message || 'فشل في حذف السجل');
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

// ✅ الموافقة على سجل وقت
export const approveTaskTimeEntrie = createAsyncThunk(
  'TaskTimeEntrie/approveTaskTimeEntrie',
  async (id: string, { rejectWithValue }) => {
    try {
      const { data } = await ntxAPI.post(`Task/ApproveTaskTimeEntry?Id=${id}`);
      if (data?.succeeded) return id;
      return rejectWithValue(data?.message || 'فشل في الموافقة على السجل');
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

// ==================== Slice ====================

interface TaskTimeEntrieState {
  taskTimeEntrieList: TaskTimeEntrie[];
  loading: boolean;
  error: string | null;
}

const initialState: TaskTimeEntrieState = {
  taskTimeEntrieList: [],
  loading: false,
  error: null
};

const TaskTimeEntrieSlice = createSlice({
  name: 'TaskTimeEntrie',
  initialState,
  reducers: {
    clearTaskTimeEntries: state => {
      state.taskTimeEntrieList = [];
      state.loading = false;
      state.error = null;
    },
    addLocalTaskTimeEntrie: (state, action: PayloadAction<TaskTimeEntrie>) => {
      state.taskTimeEntrieList.unshift(action.payload);
    },
    updateLocalTaskTimeEntrie: (state, action: PayloadAction<TaskTimeEntrie>) => {
      const i = state.taskTimeEntrieList.findIndex(e => e.id === action.payload.id);
      if (i !== -1) state.taskTimeEntrieList[i] = action.payload;
    },
    removeLocalTaskTimeEntrie: (state, action: PayloadAction<string>) => {
      state.taskTimeEntrieList = state.taskTimeEntrieList.filter(e => e.id !== action.payload);
    },

    approveLocalTaskTimeEntrie: (state, action: PayloadAction<string>) => {
      const i = state.taskTimeEntrieList.findIndex(e => e.id === action.payload);
      if (i !== -1) state.taskTimeEntrieList[i].approved = true;
    }
  },
  extraReducers: builder => {
    builder
      // 🔄 Get
      .addCase(getTaskTimeEntriesByTaskId.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getTaskTimeEntriesByTaskId.fulfilled, (state, action) => {
        state.loading = false;
        state.taskTimeEntrieList = action.payload;
      })
      .addCase(getTaskTimeEntriesByTaskId.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // ➕ Add
      .addCase(addTaskTimeEntrie.fulfilled, (state, action) => {
        state.taskTimeEntrieList.unshift(action.payload);
      })
      .addCase(addTaskTimeEntrie.rejected, (state, action) => {
        state.error = action.payload as string;
      })

      // ✏️ Update
      .addCase(updateTaskTimeEntrie.fulfilled, (state, action) => {
        const i = state.taskTimeEntrieList.findIndex(e => e.id === action.payload.id);
        if (i !== -1) state.taskTimeEntrieList[i] = action.payload;
      })
      .addCase(updateTaskTimeEntrie.rejected, (state, action) => {
        state.error = action.payload as string;
      })

      // ✅ Approve
      .addCase(approveTaskTimeEntrie.fulfilled, (state, action) => {
        const i = state.taskTimeEntrieList.findIndex(e => e.id === action.payload);
        if (i !== -1) state.taskTimeEntrieList[i].approved = true;
      })
      .addCase(approveTaskTimeEntrie.rejected, (state, action) => {
        state.error = action.payload as string;
      })

      // 🗑️ Delete
      .addCase(deleteTaskTimeEntrie.fulfilled, (state, action) => {
        state.taskTimeEntrieList = state.taskTimeEntrieList.filter(e => e.id !== action.payload);
      })
      .addCase(deleteTaskTimeEntrie.rejected, (state, action) => {
        state.error = action.payload as string;
      });
  }
});

export const { clearTaskTimeEntries, addLocalTaskTimeEntrie, updateLocalTaskTimeEntrie, removeLocalTaskTimeEntrie } =
  TaskTimeEntrieSlice.actions;

export default TaskTimeEntrieSlice.reducer;
